// useReviewQueue — single source of truth for "pending agent reviews".
//
// The sidebar badge, the AgentPanel drawer peek, and the Agents Kanban's
// "Needs Review" column all consume this hook. Without it, the badge
// undercounts because
// only `AgentTask.status === 'review_required'` flows through it — but
// ATLAS post-call completes the task and emits artefacts into separate
// tables, so the queue has to merge two sources:
//
//   1. AgentTask rows with status='review_required' (older flow: VIGIL
//      suitability, SCRIVENER letter, MASON break, EMISSARY email, etc.)
//   2. Persisted artefact rows with reviewStatus='pending' from:
//        - callExtractedFacts / callActionItems / callKbNotes
//          (ATLAS post-call — grouped per call into one queue item)
//        - wealthMonthlyNarrativeDrafts (ATLAS monthly narrative)
//
// Items are grouped at the natural unit of human review — one call's
// artefacts collapse into a single item so the IM clears them together.

import type {
    AgentFrontend,
    AgentRowTable,
    AgentTask,
    AgentTaskType,
    ArtefactSummaryRow,
    CallArtefactRow,
    MonthlyNarrativeRow,
} from '@septima/contract';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAgent } from '@/contexts/AgentContext';
import { api } from '@/lib/api';
import { artefactTableNameFor, agentRowTableFor } from '@/lib/agent-artefact-tables';
import { AGENT_NAMES, TASK_TYPE_LABELS, routeForTask } from '@/components/agent/agent-task-meta';

// -------- Item types --------

export type ReviewItemKind =
    | 'call_drafts'
    | 'monthly_narrative'
    | 'coac_action_draft'
    | 'composer_draft'
    | 'task';

/** The seven brief-based composers whose `*Draft` rows render in the
 *  generic <CompositionWorkbench> (ADR-0002 / PRD #66). */
export type ComposerDraftAgent =
    | 'SCRIVENER'
    | 'HERALD'
    | 'EMISSARY'
    | 'CHARTER'
    | 'HAVEN'
    | 'FORUM'
    | 'ARBITER';

interface ReviewItemBase {
    /** Stable id used for React keys + URL `?focus=` deep links. */
    id: string;
    kind: ReviewItemKind;
    /** Short ALL-CAPS agent name (ATLAS, VIGIL, SCRIVENER, …) for the eyebrow. */
    agent: string;
    /** Human-readable client / subject for the row title. */
    primaryLabel: string;
    /** Secondary line — call subject, period, trigger detail, etc. */
    secondaryLabel: string;
    /** Frontend scope — sidebar filters on this. */
    frontend: AgentFrontend;
    /** Page route the row's "Open in context" jumps to. */
    contextHref: string;
    /** Total pending decisions still needed inside this item. */
    pendingCount: number;
}

export interface CallDraftsReviewItem extends ReviewItemBase {
    kind: 'call_drafts';
    callId: string;
    clientId: string | null;
    factCount: number;
    actionCount: number;
    noteCount: number;
}

export interface MonthlyNarrativeReviewItem extends ReviewItemBase {
    kind: 'monthly_narrative';
    draftId: string;
    clientId: string;
    period: string;
}

export interface ComposerDraftReviewItem extends ReviewItemBase {
    kind: 'composer_draft';
    agent: ComposerDraftAgent;
    /** Id of the `*Draft` row. */
    draftId: string;
    /** The `*_drafts` session-row table the draft lives in. Constrained to the
     *  contract-owned `/api/agent/rows` allow-list so a finding table can never
     *  be dereferenced through that raw surface. */
    table: AgentRowTable;
}

export interface TaskReviewItem extends ReviewItemBase {
    kind: 'task';
    task: AgentTask;
}

/** CEDAR's coac-action-draft — one item per draft row (a single CA PDF
 *  upload can emit several). Carries the full structured field set (not the
 *  generic summary row) because the review UI lets Ops correct the extracted
 *  fields before approving (ADR-0016). */
export interface CoacActionDraftReviewItem extends ReviewItemBase {
    kind: 'coac_action_draft';
    draftId: string;
    securityName: string;
    securityId: string | null;
    eventType: string;
    sourceReferenceId: string;
    responseDeadline: string | null;
    narrative: string | null;
    wealthManager: string | null;
}

export type ReviewQueueItem =
    | CallDraftsReviewItem
    | MonthlyNarrativeReviewItem
    | CoacActionDraftReviewItem
    | ComposerDraftReviewItem
    | TaskReviewItem;

export interface UseReviewQueueResult {
    items: ReviewQueueItem[];
    totalCount: number;
    /** Item count only — equal to `items.length`. Convenience for the badge. */
    itemCount: number;
    /**
     * Has the pending-artefact fetch resolved at least once (success or error)?
     *
     * `items` is `[]` both while the queue is still loading and when it is
     * genuinely empty, and callers that act on a *specific* item — the board's
     * `?focus=` deep link — must not read the first as the second. Until this
     * flips true, "no matching item" means "not yet", not "not there".
     */
    isSettled: boolean;
}

// -------- Supporting row shapes --------
//
// The three per-stream row shapes — `CallArtefactRow`, `MonthlyNarrativeRow`,
// `ArtefactSummaryRow` — are the contract DTOs (ADR-0018). The server reads
// fully-typed ontology rows and projects them onto these, so the hook
// consumes the real types end-to-end instead of re-asserting a local shape.

// -------- Agent + frontend mapping --------
//
// AGENT_NAMES, TASK_TYPE_LABELS, TASK_TYPE_ROUTES and routeForTask now live in
// the shared agent-task-meta leaf module (imported above) — the maps used to be
// re-declared here verbatim and drifted independently. TASK_TYPE_FRONTEND below
// is NOT shared (it has no AgentTaskCard counterpart) and stays local.

/** Which frontend a given task type "belongs to" when the task row carries
 *  `frontend === 'global'`. Used to decide drawer/badge visibility — the
 *  legacy AgentBadge filter (`frontend === 'global' || frontend === current`)
 *  hides the rationale of where a global task surfaces, so we fold it in
 *  here as a deterministic mapping. */
const TASK_TYPE_FRONTEND: Record<AgentTaskType, AgentFrontend> = {
    post_call_processing: 'desk',
    call_classification_batch: 'desk',
    post_call_processing_batch: 'desk',
    fact_find_prefill_batch: 'desk',
    fact_find_document_extraction: 'desk',
    suitability_document_extraction: 'desk',
    suitability_check: 'comply',
    monthly_report: 'ops',
    monthly_narrative: 'desk',
    kyc_renewal: 'comply',
    breach_draft: 'comply',
    sanctions_screening: 'comply',
    consumer_duty_rescore: 'comply',
    reconciliation_classification_batch: 'ops',
    coac_pdf_extraction: 'ops',
    coac_breakdown: 'desk',
    suitability_letter_draft: 'desk',
    onboarding_pack_draft: 'desk',
    email_draft: 'desk',
    onboarding_pipeline_watch: 'ops',
    vulnerability_flag: 'comply',
    framework_deviation: 'desk',
    fund_policy_watch: 'view',
    data_freshness_check: 'comply',
    fund_rns_draft: 'view',
    vulnerability_adaptation_plan: 'comply',
    governance_paper_draft: 'view',
    fair_value_assessment: 'comply',
    workflow_run: 'ops',
};

/** Post-call artefacts always belong on /desk — the IM owns the review. */
const CALL_DRAFTS_FRONTEND: AgentFrontend = 'desk';

/** Monthly narrative drafts also flow through /desk (IM reviews). */
const MONTHLY_NARRATIVE_FRONTEND: AgentFrontend = 'desk';

/**
 * Pull a one-line summary from an artefact row. Tries the common
 * content fields in priority order so the hook stays generic across
 * all 17 agent types. Falls back to `null` when the row is empty
 * (e.g. the runner stamped a pointer but the artefact row is still
 * propagating).
 *
 * Exported so the Kanban / TaskCard / ActivityFeed components can
 * reuse the same fallback rules when displaying the per-task summary
 * line. Pure / pointer-stable across re-renders so it's safe to call
 * inside `useMemo` deps.
 */
export function summariseArtefact(row: Record<string, unknown> | null): string | null {
    if (!row) return null;
    const pick = (key: string): string | null => {
        const v = row[key];
        return typeof v === 'string' && v.trim() ? v.trim() : null;
    };
    return (
        pick('summary') ??
        pick('headline') ??
        pick('subject') ??
        pick('claim') ??
        pick('signalSummary') ??
        pick('ruleStatement') ??
        firstVariantBody(row)
    );
}

/** First composer variant body (truncated), read defensively off a loose row. */
function firstVariantBody(row: Record<string, unknown>): string | null {
    const variants = row.variants;
    if (!Array.isArray(variants) || variants.length === 0) return null;
    const first = variants[0];
    if (!first || typeof first !== 'object') return null;
    const body = (first as Record<string, unknown>).body;
    if (typeof body !== 'string' || !body.trim()) return null;
    const trimmed = body.trim();
    return trimmed.length > 140 ? `${trimmed.slice(0, 137)}...` : trimmed;
}

/** Read an optional string property off a loose row, normalising anything
 *  non-string to `null`. */
function loosePickString(row: Record<string, unknown>, key: string): string | null {
    const v = row[key];
    return typeof v === 'string' ? v : null;
}

/**
 * Project a loose `/api/agent/rows/:table` row (`{ id }` + catchall
 * unknown) onto the typed `ArtefactSummaryRow`. The generic agent-rows
 * surface is intentionally schema-free per table, so the summary line
 * reads only the named content scalars it knows, each defensively.
 */
function projectSummaryRow(row: { id: string }): ArtefactSummaryRow {
    const r = row as Record<string, unknown>;
    const variants = r.variants;
    return {
        id: row.id,
        reviewStatus: (() => {
            const s = r.reviewStatus;
            return s === 'pending' ||
                s === 'approved' ||
                s === 'rejected' ||
                s === 'revision_requested'
                ? s
                : null;
        })(),
        clientId: loosePickString(r, 'clientId'),
        clientName: loosePickString(r, 'clientName'),
        summary: loosePickString(r, 'summary'),
        subject: loosePickString(r, 'subject'),
        headline: loosePickString(r, 'headline'),
        claim: loosePickString(r, 'claim'),
        ruleStatement: loosePickString(r, 'ruleStatement'),
        signalSummary: loosePickString(r, 'signalSummary'),
        variants: Array.isArray(variants) ? (variants as ArtefactSummaryRow['variants']) : null,
    };
}

/**
 * The seven composer `*Draft` tables, with the persona whose review queue
 * surfaces them and the route their context link points at. The
 * <CompositionWorkbench> per-agent wrapper renders each.
 */
interface ComposerDraftSource {
    agent: ComposerDraftAgent;
    table: AgentRowTable;
    frontend: Exclude<AgentFrontend, 'global'>;
    label: string;
    contextRoute: string;
}

/** AgentTask types whose artefact is a composer `*Draft` row — PRD #67
 *  left a residual where these surfaced as a generic `task` item rather
 *  than the `composer_draft` workbench item. The task loop below routes
 *  them through `composer_draft` so each renders its CompositionWorkbench
 *  in the Agents Kanban's review panel. */
const COMPOSER_DRAFT_TASK_TYPES: ReadonlySet<AgentTaskType> = new Set<AgentTaskType>([
    'suitability_letter_draft',
    'onboarding_pack_draft',
    'email_draft',
    'fund_rns_draft',
    'vulnerability_adaptation_plan',
    'governance_paper_draft',
    'fair_value_assessment',
]);

/** AgentTask types whose reviewable artefacts are surfaced as their own
 *  dedicated review item: ATLAS post-call artefacts collapse into the per-call
 *  `call_drafts` item, monthly-narrative drafts into the `monthly_narrative`
 *  item. The AgentTask/AgentJob is only the execution envelope (CONTEXT.md —
 *  "Never a human worklist item"), so it must NOT also surface as a generic
 *  `task` card — that produced an empty duplicate ("Post-call processing")
 *  next to the real content-rich drafts item. (Composer-draft tasks are deduped
 *  separately, by artefactId, because the task can arrive before its draft row
 *  propagates; detector findings have no standalone item and DO surface through
 *  their task, so they are intentionally excluded here.) */
const ARTEFACT_SURFACED_TASK_TYPES: ReadonlySet<AgentTaskType> = new Set<AgentTaskType>([
    'post_call_processing',
    'post_call_processing_batch',
    'monthly_narrative',
    // CEDAR's coac_pdf_extraction job can emit N CoacActionDraft artefacts per
    // task; each is already surfaced individually as a `coac_action_draft`
    // item off the `coacActionDrafts` table below.
    'coac_pdf_extraction',
]);

const COMPOSER_DRAFT_SOURCES: ComposerDraftSource[] = [
    {
        agent: 'SCRIVENER',
        table: 'suitability_letter_drafts',
        // SCRIVENER is an IM-facing composer (agent persona: desk) — the
        // Investment Manager reviews/edits/approves the suitability draft.
        // Compliance gets visibility elsewhere; primary review is the desk.
        frontend: 'desk',
        label: 'Suitability letter',
        contextRoute: '/desk/agents',
    },
    {
        agent: 'HERALD',
        table: 'onboarding_pack_drafts',
        frontend: 'desk',
        label: 'Onboarding pack',
        contextRoute: '/desk/agents',
    },
    {
        agent: 'EMISSARY',
        table: 'client_communication_drafts',
        frontend: 'desk',
        label: 'Client communication',
        contextRoute: '/desk/agents',
    },
    {
        agent: 'CHARTER',
        table: 'fund_report_drafts',
        frontend: 'view',
        label: 'Fund report',
        contextRoute: '/view/agents',
    },
    {
        agent: 'HAVEN',
        table: 'vulnerability_adaptation_plan_drafts',
        frontend: 'comply',
        label: 'Vulnerability adaptation plan',
        contextRoute: '/comply/agents',
    },
    {
        agent: 'FORUM',
        table: 'governance_paper_drafts',
        frontend: 'view',
        label: 'Governance paper',
        contextRoute: '/view/agents',
    },
    {
        agent: 'ARBITER',
        table: 'fair_value_assessment_drafts',
        frontend: 'comply',
        label: 'Fair-value assessment',
        contextRoute: '/comply/agents',
    },
];

/** Narrow shape for a composer `*Draft` row — only the queue-relevant
 *  fields. The full row is re-fetched by the workbench card itself. A
 *  subset of `ArtefactSummaryRow` so the composer streams re-tag onto it
 *  without a cast. */
interface ComposerDraftRow {
    id: string;
    clientId?: string | null;
    reviewStatus?: 'pending' | 'approved' | 'rejected' | 'revision_requested' | null;
}

// Drop a trailing "· Run <AGENT> now" call-to-action clause from a meta label
// (e.g. an unimplemented task's `triggerDetail` of "Ops · Run MASON now"),
// leaving just the leading area/persona ("Ops"). Applied once at the source so
// every surface that renders `secondaryLabel` (review panel, kanban card,
// rejection dialog subject) stays clean.
function stripRunNowCta(label: string | null | undefined): string {
    const value = label ?? '';
    const parts = value.split('·');
    if (parts.length > 1 && /run .* now/i.test(parts[parts.length - 1])) {
        return parts.slice(0, -1).join('·').trim();
    }
    return value;
}

// -------- Hook --------

export function useReviewQueue(frontend?: AgentFrontend): UseReviewQueueResult {
    // The review queue is part of the owner-scoped worklist (ADR-0022), so the
    // task-derived review items use `worklistTasks` (runs I own / ownerless on
    // my surface), not the firm-wide activity feed.
    const { worklistTasks: tasks } = useAgent();

    // Single typed call replaces 14 generic `/api/db/:table` reads. The
    // server-side aggregator filters every artefact source to
    // `reviewStatus = 'pending'` and denormalises `clientName` so the
    // hook does not need a separate ontology_clients lookup.
    const { data, isFetched } = useQuery({
        queryKey: ['review-queue', 'pending'],
        queryFn: async () => {
            const res = await api.reviewQueue.pending();
            if (res.status !== 200) {
                throw new Error(`review-queue fetch failed: ${res.status}`);
            }
            return res.body;
        },
        refetchInterval: 10_000,
    });

    const factRows = data?.callExtractedFacts ?? [];
    const actionRows = data?.callActionItems ?? [];
    const noteRows = data?.callKbNotes ?? [];
    const narrativeRows = data?.wealthMonthlyNarrativeDrafts ?? [];
    const coacActionDraftRows = data?.coacActionDrafts ?? [];
    const vigilRows = data?.wealthVigilFindings ?? [];
    const suitLetterRows = data?.suitabilityLetterDrafts ?? [];
    const onbPackRows = data?.onboardingPackDrafts ?? [];
    const clientCommRows = data?.clientCommunicationDrafts ?? [];
    const onbStallRows = data?.onboardingStallFindings ?? [];
    const vulnMarkerRows = data?.vulnerabilityMarkerFindings ?? [];
    const pcfDevRows = data?.pcfDeviationFindings ?? [];
    const trustPolicyRows = data?.investmentTrustPolicyFindings ?? [];
    const freshnessFindingRows = data?.dataFreshnessFindings ?? [];
    const fundReportRows = data?.fundReportDrafts ?? [];
    const vulnPlanRows = data?.vulnerabilityAdaptationPlanDrafts ?? [];
    const govPaperRows = data?.governancePaperDrafts ?? [];
    const fairValueRows = data?.fairValueAssessmentDrafts ?? [];

    // Build a per-table id → row map so task → artefact lookup is O(1).
    // `register` accepts any of the three contract row DTOs: the post-call
    // and narrative rows (CallArtefactRow / MonthlyNarrativeRow) are
    // structurally a superset of the summary fields the lookup reads, so
    // they index alongside the summary rows with no cast.
    const artefactRowByTable = useMemo(() => {
        const m = new Map<string, Map<string, ArtefactSummaryRow>>();
        const register = (
            table: string,
            rows: ReadonlyArray<ArtefactSummaryRow | CallArtefactRow | MonthlyNarrativeRow>,
        ) => {
            const byId = new Map<string, ArtefactSummaryRow>();
            for (const r of rows) byId.set(r.id, r);
            m.set(table, byId);
        };
        register('wealth_vigil_findings', vigilRows);
        register('suitability_letter_drafts', suitLetterRows);
        register('onboarding_pack_drafts', onbPackRows);
        register('client_communication_drafts', clientCommRows);
        register('onboarding_stall_findings', onbStallRows);
        register('vulnerability_marker_findings', vulnMarkerRows);
        register('pcf_deviation_findings', pcfDevRows);
        register('investment_trust_policy_findings', trustPolicyRows);
        register('data_freshness_findings', freshnessFindingRows);
        register('fund_report_drafts', fundReportRows);
        register('vulnerability_adaptation_plan_drafts', vulnPlanRows);
        register('governance_paper_drafts', govPaperRows);
        register('fair_value_assessment_drafts', fairValueRows);
        // Post-call + narrative tables are already loaded above for the
        // grouped call_drafts / monthly_narrative items; surface them here
        // too so individual-task lookups by `artefactType` work uniformly.
        register('call_extracted_facts', factRows);
        register('call_action_items', actionRows);
        register('call_kb_notes', noteRows);
        register('wealth_monthly_narrative_drafts', narrativeRows);
        return m;
    }, [
        vigilRows,
        suitLetterRows,
        onbPackRows,
        clientCommRows,
        onbStallRows,
        vulnMarkerRows,
        pcfDevRows,
        trustPolicyRows,
        freshnessFindingRows,
        fundReportRows,
        vulnPlanRows,
        govPaperRows,
        fairValueRows,
        factRows,
        actionRows,
        noteRows,
        narrativeRows,
    ]);

    // Dereference a task to its artefact row, if any.
    const artefactFor = useMemo(() => {
        return (task: AgentTask): ArtefactSummaryRow | null => {
            const type = task.artefactType ?? null;
            const id = task.artefactId ?? null;
            if (!type || !id) return null;
            const table = artefactTableNameFor(type);
            if (!table) return null;
            return artefactRowByTable.get(table)?.get(id) ?? null;
        };
    }, [artefactRowByTable]);

    // Composer `*Draft` rows are already in the aggregator payload above —
    // narrow to ComposerDraftRow (a subset of ArtefactSummaryRow) for the
    // per-agent rendering loop; reviewStatus was filtered to pending by the
    // server.
    const scrivenerRows: ReadonlyArray<ComposerDraftRow> = suitLetterRows;
    const heraldRows: ReadonlyArray<ComposerDraftRow> = onbPackRows;
    const emissaryRows: ReadonlyArray<ComposerDraftRow> = clientCommRows;
    const charterRows: ReadonlyArray<ComposerDraftRow> = fundReportRows;
    const havenRows: ReadonlyArray<ComposerDraftRow> = vulnPlanRows;
    const forumRows: ReadonlyArray<ComposerDraftRow> = govPaperRows;
    const arbiterRows: ReadonlyArray<ComposerDraftRow> = fairValueRows;

    // Client name lookup — keyed by id so call_drafts / monthly_narrative
    // items can show the client without each consumer rebuilding the map.
    // Server denormalises `clientName` onto every row, so we just project
    // it back into the lookup map.
    const clientNameById = useMemo(() => {
        const m = new Map<string, string>();
        const ingest = (rows: Array<{ clientId?: string | null; clientName?: string | null }>) => {
            for (const r of rows) {
                if (r.clientId && r.clientName) m.set(r.clientId, r.clientName);
            }
        };
        ingest(factRows);
        ingest(actionRows);
        ingest(noteRows);
        ingest(narrativeRows);
        ingest(vigilRows);
        ingest(suitLetterRows);
        ingest(onbPackRows);
        ingest(clientCommRows);
        ingest(onbStallRows);
        ingest(vulnMarkerRows);
        ingest(pcfDevRows);
        ingest(trustPolicyRows);
        ingest(freshnessFindingRows);
        ingest(fundReportRows);
        ingest(vulnPlanRows);
        ingest(govPaperRows);
        ingest(fairValueRows);
        return m;
    }, [
        factRows,
        actionRows,
        noteRows,
        narrativeRows,
        vigilRows,
        suitLetterRows,
        onbPackRows,
        clientCommRows,
        onbStallRows,
        vulnMarkerRows,
        pcfDevRows,
        trustPolicyRows,
        freshnessFindingRows,
        fundReportRows,
        vulnPlanRows,
        govPaperRows,
        fairValueRows,
    ]);

    const items = useMemo<ReviewQueueItem[]>(() => {
        const out: ReviewQueueItem[] = [];

        // --- Call drafts (grouped per call) ---
        interface CallSlot {
            callId: string;
            clientId: string | null;
            // Server denormalises the DeskCall's display name onto every
            // artefact row — present even when the call's clientId is still
            // unassigned. Prefer it over the clientId→name lookup below.
            clientName: string | null;
            factCount: number;
            actionCount: number;
            noteCount: number;
        }
        const callSlots = new Map<string, CallSlot>();
        const tallyArtefact = (row: CallArtefactRow, kind: 'fact' | 'action' | 'note') => {
            // Server already filtered to pending / unstamped (null). Keep the
            // belt-and-braces guard so a non-pending row never tallies.
            if (row.reviewStatus !== 'pending' && row.reviewStatus != null) return;
            if (!row.callId) return;
            const slot = callSlots.get(row.callId) ?? {
                callId: row.callId,
                clientId: row.clientId ?? null,
                clientName: row.clientName ?? null,
                factCount: 0,
                actionCount: 0,
                noteCount: 0,
            };
            if (kind === 'fact') slot.factCount += 1;
            else if (kind === 'action') slot.actionCount += 1;
            else slot.noteCount += 1;
            if (!slot.clientId && row.clientId) slot.clientId = row.clientId;
            if (!slot.clientName && row.clientName) slot.clientName = row.clientName;
            callSlots.set(row.callId, slot);
        };
        for (const r of factRows) tallyArtefact(r, 'fact');
        for (const r of actionRows) tallyArtefact(r, 'action');
        for (const r of noteRows) tallyArtefact(r, 'note');

        for (const slot of callSlots.values()) {
            const pendingCount = slot.factCount + slot.actionCount + slot.noteCount;
            if (pendingCount === 0) continue;
            const clientName =
                slot.clientName ??
                (slot.clientId ? clientNameById.get(slot.clientId) : null) ??
                'Unknown client';
            const summary = [
                slot.factCount > 0 && `${slot.factCount} fact${slot.factCount === 1 ? '' : 's'}`,
                slot.actionCount > 0 &&
                    `${slot.actionCount} action${slot.actionCount === 1 ? '' : 's'}`,
                slot.noteCount > 0 && `${slot.noteCount} note${slot.noteCount === 1 ? '' : 's'}`,
            ]
                .filter(Boolean)
                .join(' · ');
            out.push({
                kind: 'call_drafts',
                id: `call:${slot.callId}`,
                agent: 'ATLAS',
                primaryLabel: clientName,
                secondaryLabel: `Post-call drafts · ${summary}`,
                frontend: CALL_DRAFTS_FRONTEND,
                contextHref: `/desk/calls-meetings?call=${encodeURIComponent(slot.callId)}`,
                pendingCount,
                callId: slot.callId,
                clientId: slot.clientId,
                factCount: slot.factCount,
                actionCount: slot.actionCount,
                noteCount: slot.noteCount,
            });
        }

        // --- Monthly narrative drafts ---
        for (const n of narrativeRows) {
            if (n.reviewStatus !== 'pending' && n.reviewStatus != null) continue;
            const clientName = clientNameById.get(n.clientId) ?? 'Unknown client';
            out.push({
                kind: 'monthly_narrative',
                id: `narrative:${n.id}`,
                agent: 'ATLAS',
                primaryLabel: clientName,
                secondaryLabel: `Monthly narrative · ${n.period}`,
                frontend: MONTHLY_NARRATIVE_FRONTEND,
                contextHref: `/desk/client-knowledge?client=${encodeURIComponent(n.clientId)}`,
                pendingCount: 1,
                draftId: n.id,
                clientId: n.clientId,
                period: n.period,
            });
        }

        // --- CEDAR coac-action-drafts (one item per draft row — a single CA
        // PDF can yield several) ---
        for (const d of coacActionDraftRows) {
            if (d.reviewStatus !== 'pending' && d.reviewStatus != null) continue;
            out.push({
                kind: 'coac_action_draft',
                id: `coac-draft:${d.id}`,
                agent: 'CEDAR',
                primaryLabel: d.securityName,
                secondaryLabel: `CA extraction · ${d.eventType}`,
                frontend: 'ops',
                contextHref: `/ops/corporate-actions?focus=${encodeURIComponent(`coac-draft:${d.id}`)}`,
                pendingCount: 1,
                draftId: d.id,
                securityName: d.securityName,
                securityId: d.securityId,
                eventType: d.eventType,
                sourceReferenceId: d.sourceReferenceId,
                responseDeadline: d.responseDeadline,
                narrative: d.narrative,
                wealthManager: d.wealthManager,
            });
        }

        // --- Composer `*Draft` rows pending review (ADR-0002 / PRD #66) ---
        const composerRowsByTable: Record<string, ReadonlyArray<ComposerDraftRow>> = {
            suitability_letter_drafts: scrivenerRows,
            onboarding_pack_drafts: heraldRows,
            client_communication_drafts: emissaryRows,
            fund_report_drafts: charterRows,
            vulnerability_adaptation_plan_drafts: havenRows,
            governance_paper_drafts: forumRows,
            fair_value_assessment_drafts: arbiterRows,
        };
        /** Composer source keyed by its `*_drafts` table — used by the
         *  AgentTask loop below to route composer-draft tasks to the
         *  workbench item. */
        const composerSourceByTable = new Map<string, ComposerDraftSource>();
        for (const s of COMPOSER_DRAFT_SOURCES) composerSourceByTable.set(s.table, s);

        /** Draft-ids already surfaced as `composer_draft` items from the
         *  `*_drafts` tables — the AgentTask loop dedups against this so a
         *  composer draft never appears twice (once from the table, once
         *  from its task envelope). */
        const emittedDraftIds = new Set<string>();

        for (const source of COMPOSER_DRAFT_SOURCES) {
            const rows = composerRowsByTable[source.table] ?? [];
            for (const d of rows) {
                if (d.reviewStatus !== 'pending' && d.reviewStatus != null) continue;
                const clientName = d.clientId
                    ? (clientNameById.get(d.clientId) ?? source.label)
                    : source.label;
                const itemId = `draft:${d.id}`;
                emittedDraftIds.add(d.id);
                out.push({
                    kind: 'composer_draft',
                    id: itemId,
                    agent: source.agent,
                    primaryLabel: clientName,
                    secondaryLabel: `${source.label} draft`,
                    frontend: source.frontend,
                    // `?focus=` matches the review-item id, so prefix it the same
                    // way — the queue auto-expands the workbench on landing.
                    contextHref: `${source.contextRoute}?focus=${encodeURIComponent(itemId)}`,
                    pendingCount: 1,
                    draftId: d.id,
                    table: source.table,
                });
            }
        }

        // --- AgentTask rows still waiting on review ---
        for (const task of tasks) {
            if (task.status !== 'review_required') continue;
            // Skip the bare task envelope for agents whose artefacts already
            // appear as a dedicated item (call_drafts / monthly_narrative),
            // so a single ATLAS run shows once, not twice.
            if (ARTEFACT_SURFACED_TASK_TYPES.has(task.type)) continue;
            const agent = AGENT_NAMES[task.type] ?? task.type;
            const typeLabel = TASK_TYPE_LABELS[task.type] ?? task.type;
            const taskFrontend: AgentFrontend =
                task.frontend === 'global' ? TASK_TYPE_FRONTEND[task.type] : task.frontend;
            const artefactRow = artefactFor(task);

            // PRD #67 residual fix: composer-draft tasks must surface as the
            // `composer_draft` workbench item, not a generic `task` row. If the
            // task carries an artefact pointer into a `*_drafts` table, route
            // it through `composer_draft`. When the draft row was already
            // emitted from its table above, skip the task (dedup); when it has
            // not yet propagated, emit the workbench item off the task pointer.
            const draftTable = artefactTableNameFor(task.artefactType ?? null);
            const composerSource = draftTable ? composerSourceByTable.get(draftTable) : undefined;
            if (COMPOSER_DRAFT_TASK_TYPES.has(task.type) && composerSource && task.artefactId) {
                if (emittedDraftIds.has(task.artefactId)) continue;
                emittedDraftIds.add(task.artefactId);
                const itemId = `draft:${task.artefactId}`;
                out.push({
                    kind: 'composer_draft',
                    id: itemId,
                    agent: composerSource.agent,
                    primaryLabel: task.clientName ?? composerSource.label,
                    secondaryLabel: `${composerSource.label} draft`,
                    frontend: composerSource.frontend,
                    contextHref: `${composerSource.contextRoute}?focus=${encodeURIComponent(itemId)}`,
                    pendingCount: 1,
                    draftId: task.artefactId,
                    table: composerSource.table,
                });
                continue;
            }

            // Prefer the artefact's emitted summary over the templated
            // `triggerDetail` (PRD #65 user story 1) — falling back to the
            // legacy field for AEGIS/MASON/CEDAR tasks whose artefacts live
            // on legacy tables not yet ported.
            const artefactSummary = summariseArtefact(artefactRow);
            const secondary = stripRunNowCta(
                artefactSummary
                    ? artefactSummary
                    : task.clientName
                      ? typeLabel
                      : task.triggerDetail,
            );
            out.push({
                kind: 'task',
                id: `task:${task.id}`,
                agent,
                primaryLabel: task.clientName ?? typeLabel,
                secondaryLabel: secondary,
                frontend: taskFrontend,
                contextHref: routeForTask(task),
                pendingCount: 1,
                task,
            });
        }

        // Apply frontend filter last so the underlying data work runs once.
        if (frontend) {
            return out.filter((it) => it.frontend === frontend);
        }
        return out;
    }, [
        factRows,
        actionRows,
        noteRows,
        narrativeRows,
        coacActionDraftRows,
        scrivenerRows,
        heraldRows,
        emissaryRows,
        charterRows,
        havenRows,
        forumRows,
        arbiterRows,
        clientNameById,
        tasks,
        frontend,
        artefactFor,
    ]);

    const totalCount = useMemo(() => items.reduce((sum, it) => sum + it.pendingCount, 0), [items]);

    return { items, totalCount, itemCount: items.length, isSettled: isFetched };
}

/**
 * Resolve a single AgentTask to its artefact row's summary line.
 * Returns `null` when the task has no artefact pointer or the
 * artefact row hasn't propagated to the client yet — callers fall
 * back to `task.triggerDetail` in that case.
 *
 * Implemented as a thin React Query wrapper keyed off the task's
 * `artefactType`, so it issues at most one round-trip per (table,
 * session). Components used inside the same provider that calls
 * `useReviewQueue` benefit from React Query's request coalescing.
 */
export function useArtefactSummary(task: AgentTask): string | null {
    // `agentRowTableFor` (not `artefactTableNameFor`) so only tables the raw
    // `/api/agent/rows/:table` surface actually serves resolve to non-null:
    // finding tasks map to null here, skip the query (which 400s for them), and
    // fall back to the trigger detail — exactly as they already do today.
    const table = agentRowTableFor(task.artefactType ?? null);
    const { data: rows = [] } = useQuery<ArtefactSummaryRow[]>({
        queryKey: ['agent', 'rows', table ?? '<none>'],
        queryFn: async () => {
            if (!table) return [];
            const res = await api.agent.listAgentRows({
                params: { table },
                query: {},
            });
            if (res.status !== 200) throw new Error(`fetch ${res.status}`);
            // `/api/agent/rows/:table` is the generic agent-rows surface — its
            // contract row is `{ id }` + catchall unknown. Project each row
            // onto the summary DTO field-by-field (every content field is an
            // optional string, read defensively) so the summary line stays
            // typed without asserting a shape this loose endpoint never
            // promises.
            return res.body.rows.map(projectSummaryRow);
        },
        enabled: table != null,
    });
    return useMemo(() => {
        if (!table || !task.artefactId) return null;
        const row = rows.find((r) => r.id === task.artefactId) ?? null;
        return summariseArtefact(row);
    }, [rows, table, task.artefactId]);
}
